import { getSite } from '@/utils/getSite';
import { isVideoPage } from '@/utils/isVideoPage';
import { logger } from '@/utils/logger';
import { checkForUserConsentAndGetAdConfig } from '../ads/userContentAdsConfig';
import { AD_SCHEDULE_ID } from '../constants';
import { floatBehavior, seo, videoAdWatch } from '../features';
import { VideoPlayerType } from '../types/video';

export const setupPlayer = async (params: VideoPlayerType) => {
  const {
    videoId: mediaId,
    playerId,
    autostart,
    articleInfo,
    ypPartId = null,
    verticalFloatingMode,
    hideAds,
    isNativeAd,
    hasScrollArticles,
  } = params;

  const player = window.jwplayer(document.getElementById(`video-${mediaId}`));

  const jwDefaults = window.jwDefaults;
  const customAdConfig = await checkForUserConsentAndGetAdConfig({
    autostart,
    articleInfo,
    ypPartId,
    isNativeAd,
    isVerticalVideo: verticalFloatingMode,
    hideAds,
    hasScrollArticles,
  });

  const playerEnv = player.getEnvironment();
  const isMobile = playerEnv?.OS?.mobile;

  const features = [floatBehavior, videoAdWatch, seo].map((setup) => setup(player, params, isMobile));

  const configOverrides = features.reduce((configValues, feature) => {
    return {
      ...configValues,
      setTimeEvents: true, //for JW 3s, 10s, 15 sec analytics
      ...feature.preConfig(),
    };
  }, {});

  const isAutoStart = autostart === 'viewable';
  const adsScheduleIdForSites = isAutoStart ? AD_SCHEDULE_ID.ATP : AD_SCHEDULE_ID.CTP;
  const adScheduleType = isVideoPage() ? AD_SCHEDULE_ID.VP : adsScheduleIdForSites;

  const site = getSite() as keyof typeof adScheduleType;

  const videoPlayerConfig = {
    ...params,
    aspectratio: params.aspectRatio,
    advertising: {
      ...jwDefaults.advertising,
      ...customAdConfig,
      adscheduleid: adScheduleType[site],
    },
    pid: playerId,
    autoPause: {
      viewability: true,
      pauseAds: true,
    },
    ...configOverrides,
  };

  if (hideAds) {
    videoPlayerConfig.advertising = {};
  } else if (isNativeAd || (!['motherhood', 'mabra'].includes(site) && (customAdConfig as any).noConsent)) {
    // Show ads with or without (limited) consent only for motherhood and mabra sites
    videoPlayerConfig.advertising = { ...jwDefaults.advertising };
  }

  logger.debug('JW Video Player advertising Config:', videoPlayerConfig.advertising);

  player.setup(videoPlayerConfig).on('ready', (/*{setupTime: any}*/) => {
    features.forEach((feature) => feature.onReady());
  });

  //Add cts
  window.cts = window.cts || {};
  window.cts.jwplayer = window.cts.jwplayer || [];
  window.cts.jwplayer.push({ ...player });

  return player;
};
